/* eslint-disable @next/next/no-img-element -- CMS icons are runtime URLs outside next/image allowlists. */

import type { IGroupInfo, IProductGroupTrustModuleType } from '@/api/types/product';
import Faq from '@/components/Faq';
import LegacyRichText from './LegacyRichText';
import SeoContentCarousel from './SeoContentCarousel.client';
import { createGoodsListSeoFooterViewModel } from './goodsListSeoFooter.utils';

interface GoodsListSeoFooterLabels {
  next: string;
  previous: string;
  slide: string;
}

interface GoodsListSeoFooterProps {
  groupInfo: IGroupInfo;
  labels: GoodsListSeoFooterLabels;
}

const SEO_FOOTER_ASSET_ROOT = '/zeelool-next/goods-list/seo-footer';

const TRUST_MODULE_FALLBACK_ICONS: Partial<Record<IProductGroupTrustModuleType, string>> = {
  free_shipping: `${SEO_FOOTER_ASSET_ROOT}/free-shipping.svg`,
  warranty: `${SEO_FOOTER_ASSET_ROOT}/warranty.svg`,
  why_choose: `${SEO_FOOTER_ASSET_ROOT}/why-choose.svg`,
};

export default function GoodsListSeoFooter({ groupInfo, labels }: GoodsListSeoFooterProps) {
  const { faqItems, faqTitle, hOne, seoContentBlocks, trustModules } =
    createGoodsListSeoFooterViewModel(groupInfo);
  const hasSeoHeading = Boolean(hOne);
  const hasSeoCarousel = seoContentBlocks.length > 0;
  const hasTrustModules = trustModules.length > 0;
  const hasFaq = faqItems.length > 0;

  if (!hasSeoHeading && !hasSeoCarousel && !hasTrustModules && !hasFaq) return null;

  return (
    <section className="mt-[24px] bg-[#f2f3f5] px-[14px] py-[32px] text-primary-black min-[920px]:mt-0 min-[920px]:px-[24px] min-[920px]:py-[72px]">
      <div className="mx-auto flex w-full max-w-[1024px] flex-col gap-[19px] min-[920px]:gap-[48px]">
        {(hasSeoHeading || hasSeoCarousel) && (
          <div className="flex flex-col gap-[19px] min-[920px]:gap-[32px]">
            {hasSeoHeading && (
              <h1 className="text-center font-bb text-[20px] leading-[24px] min-[920px]:text-[32px] min-[920px]:leading-[39px]">
                <LegacyRichText value={hOne} />
              </h1>
            )}

            {hasSeoCarousel && (
              <SeoContentCarousel labels={labels}>
                {seoContentBlocks.map((block) => (
                  <article
                    key={`${block.sort}-${block.title}`}
                    className="flex min-h-full w-full flex-col gap-[16px] break-words px-[12px] py-[24px] min-[920px]:gap-[12px] min-[920px]:px-[24px] min-[920px]:py-[32px]"
                  >
                    <h2 className="text-center font-bb text-[14px] leading-[17px] min-[920px]:text-[16px] min-[920px]:leading-[19px]">
                      {block.title}
                    </h2>
                    <p className="mx-auto w-full max-w-[808px] break-words whitespace-pre-line font-ir text-[12px] leading-[18px]">
                      <LegacyRichText value={block.content} allowLinks />
                    </p>
                  </article>
                ))}
              </SeoContentCarousel>
            )}
          </div>
        )}

        {hasTrustModules && (
          <ul className="flex flex-col gap-[24px] py-[20px] min-[920px]:grid min-[920px]:auto-cols-fr min-[920px]:grid-flow-col min-[920px]:grid-rows-[56px_auto_1fr] min-[920px]:gap-x-[24px] min-[920px]:gap-y-[12px] min-[920px]:px-[32px] min-[920px]:py-0">
            {trustModules.map((item, index) => {
              const icon = item.icon || TRUST_MODULE_FALLBACK_ICONS[item.type];

              return (
                <li
                  key={`${item.type}-${item.sort}-${index}`}
                  className="flex items-center gap-[12px] min-[920px]:row-span-3 min-[920px]:grid min-[920px]:w-full min-[920px]:grid-rows-subgrid min-[920px]:items-start min-[920px]:justify-items-center min-[920px]:text-center"
                >
                  {icon ? (
                    <img
                      src={icon}
                      alt=""
                      width={56}
                      height={56}
                      loading="lazy"
                      className="size-[44px] shrink-0 object-contain min-[920px]:size-[56px]"
                    />
                  ) : null}
                  <div className="flex w-full min-w-0 max-w-[287px] flex-col gap-[6px] min-[920px]:row-span-2 min-[920px]:row-start-2 min-[920px]:grid min-[920px]:max-w-[274px] min-[920px]:grid-rows-subgrid min-[920px]:text-center">
                    <p className="font-bb text-[13px] leading-[17px] min-[920px]:text-[14px]">
                      {item.title}
                    </p>
                    <p className="font-br text-[11px] leading-[normal] min-[920px]:text-[12px] min-[920px]:leading-[14px]">
                      {item.description}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {hasFaq && (
          <Faq
            title={faqTitle}
            items={faqItems.map((item) => ({
              key: item.question,
              question: <LegacyRichText value={item.question} />,
              answer: item.answer ? <LegacyRichText value={item.answer} allowLinks /> : null,
            }))}
          />
        )}
      </div>
    </section>
  );
}
